"use client"

import { Eye, EyeOff } from "lucide-react"
import type React from "react"
import { useState } from "react"


interface InputFieldProps {
  type: string
  label: string
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  error?: string
  showPasswordToggle?: boolean
  placeholder?: string
  icon?: React.ReactNode
  maxLength?: number
  max?: string
  className?: string
}

const InputField: React.FC<InputFieldProps> = ({
  type,
  label,
  name,
  value,
  onChange,
  required = false,
  error,
  showPasswordToggle = false,
  placeholder,
  icon,
  maxLength,
  max,
  className = "",
}) => {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className={`space-y-3 ${className}`}>
      <label htmlFor={name} className="block text-xs font-bold text-gray-700">
        {label} {required && <span className="text-red-500 text-[10px]">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10 text-gray-500">{icon}</div>
        )}
        <input
          type={showPasswordToggle && showPassword ? "text" : type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className={`block w-full ${icon ? "pl-8" : "pl-2.5"} ${
            showPasswordToggle ? "pr-8" : "pr-2.5"
          } py-2 border border-white/50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400 transition-all duration-300 bg-white/80 backdrop-blur-sm shadow-sm text-gray-900 placeholder-gray-500 ${
            error ? "border-red-300 focus:ring-red-500/50 bg-red-50/80" : "hover:border-blue-300/50"
          }`}
          required={required}
          placeholder={placeholder}
          maxLength={maxLength}
          max={max}
          style={{
            fontSize: '13px' // Prevents zoom on iOS
          }}
        />
        {showPasswordToggle && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-blue-600 transition-all duration-300 focus:outline-none"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
      {error && (
        <div className="flex items-center space-x-1.5 text-xs text-red-600 bg-red-50/80 backdrop-blur-sm p-1.5 rounded-md border border-red-200/50 shadow-sm">
          <svg className="w-2.5 h-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export default InputField
